import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TQuizQuestion } from '@onesource/services';

interface MultipleChoiceQuestionProps {
  question: TQuizQuestion;
  selectedOptionIds: string[];
  onSelect: (optionIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Multiple Choice Question Component
 * Renders option cards with checkbox-style indicators and letter labels (A, B, C, D)
 */
export const MultipleChoiceQuestion = ({
  question,
  selectedOptionIds,
  onSelect,
  disabled = false,
}: MultipleChoiceQuestionProps) => {
  const sortedOptions = [...question.options].sort((a, b) => a.position - b.position);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedOptions.map((option, index) => {
        const isSelected = selectedOptionIds.includes(option.id);
        const optionLetter = String.fromCodePoint(65 + index); // A, B, C, D...

        return (
          <Box
            key={option.id}
            onClick={() => !disabled && onSelect([option.id])}
            sx={{
              p: 2,
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              cursor: disabled ? 'default' : 'pointer',
              transition: 'all 0.2s',
              border: '2px solid',
              borderColor: isSelected ? '#ff4d6d' : 'transparent',
              '&:hover': disabled
                ? {}
                : {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  },
            }}
          >
            {/* Checkbox circle */}
            <Box
              sx={{
                width: 20,
                height: 20,
                border: '2px solid',
                borderColor: isSelected ? '#ff4d6d' : '#ccc',
                borderRadius: '4px',
                bgcolor: isSelected ? '#ff4d6d' : 'white',
                flexShrink: 0,
                mt: 0.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: 'white',
                    borderRadius: '2px',
                  }}
                />
              )}
            </Box>

            {/* Option letter + text */}
            <Typography sx={{ lineHeight: 1.5 }}>
              <Box component="span" sx={{ color: '#ff4d6d', fontWeight: 'bold' }}>
                {optionLetter})
              </Box>{' '}
              {option.optionText}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
